import { useBulkResolveContacts } from "@components/virtual-receptionist/contacts/hooks/useBulkResolveContacts";
import { buildChatDraftKey } from "../../../chat-dock/chatDrafts";
import { useVirtualAssistant } from "@components/virtual-receptionist/context/VirtualAssistantContext";
import { useContactCenterTheme } from "@components/virtual-receptionist/theming/ContactCenterThemeContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, IconButton, ListItemText, MenuItem, MenuList, Popover, Tooltip, Typography } from "@mui/material";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { formatUSPhone } from "src/utils";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { SmsContact } from "../contacts/types/SmsContact";
import { AudioMessageBubble } from "./components/AudioMessageBubble";
import { getSmsStatusTooltip, MessageDeliveryStatus } from "./components/MessageDeliveryStatus";
import { MessageInput } from "./components/MessageInput";
import { SmsSearchResultsList } from "./components/SmsChatResultList";
import { useMarkAsRead } from "../contacts/hooks/useMarkAsRead";
import { useChatSmsMutation } from "./hooks/useChatSmsMutation";
import { useChatSmsInfinite } from "./hooks/useChatSmsQuery";
import { useResendChatSmsMutation } from "./hooks/useResendChatSmsMutation";
import { SmsMessage } from "./types/SmsMessage";

const MAX_FOCUS_PAGE_FETCHES = 20;
const MAX_SCROLL_ATTEMPTS = 6;
const SCROLL_RETRY_DELAY_MS = 80;

interface Props {
  contact: SmsContact | null;
  onBack: () => void;
  sessionNumber: string;
  companyId?: number | string | null;
  onQuickSave?: (phoneNumber: string, anchorEl: HTMLElement) => void;
  onEditContact?: (contact: SmsContact) => void;
  hideBack?: boolean;
  isActive?: boolean;
  focusMessageUid?: string | null;
  focusMessageText?: string | null;
  focusMessageRequestKey?: string | null;
  onFocusMessageConsumed?: () => void;
}

function formatDayLabel(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDay.getTime() === today.getTime()) return "Today";
  if (msgDay.getTime() === yesterday.getTime()) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: msgDay.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
}

function getDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function isAudioUrl(url: string): boolean {
  const normalizedUrl = String(url).toLowerCase();
  return normalizedUrl.includes("audio") || /\.(mp3|ogg|wav|webm|aac|amr|m4a)(\?|$)/i.test(normalizedUrl);
}

function isVideoUrl(url: string): boolean {
  const normalizedUrl = String(url).toLowerCase();
  return normalizedUrl.includes("video") || /\.(mp4|webm|ogg|mov|3gp)(\?|$)/i.test(normalizedUrl);
}

export function SmsChatView({
  contact,
  onBack,
  sessionNumber,
  companyId,
  onQuickSave,
  onEditContact,
  hideBack,
  isActive = true,
  focusMessageUid,
  focusMessageText,
  focusMessageRequestKey,
  onFocusMessageConsumed,
}: Props) {
  const { theme } = useContactCenterTheme();
  const { data: bulkResolvedMap = {} } = useBulkResolveContacts(contact?.phoneNumber ? [contact.phoneNumber] : []);
  const resolved = contact?.phoneNumber ? bulkResolvedMap[contact.phoneNumber] : null;

  const savedContact = contact?.contact ? contact.contact : resolved ? { ...resolved, isResolved: true } : null;
  const displayName = savedContact
    ? (() => {
        const firstName = savedContact.firstName || savedContact.name || "";
        const lastName = savedContact.lastName || "";
        return [firstName, lastName].filter(Boolean).join(" ");
      })()
    : null;

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText.trim(), 400);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { showSnackbar } = useGlobalSnackbar();
  const { makeCall } = useVirtualAssistant();

  const isSearchingMessages = searchOpen && Boolean(debouncedSearchText);

  const chatQuery = useChatSmsInfinite(contact?.phoneNumber, sessionNumber, companyId, 20, "", true);
  const chatQueryRef = useRef(chatQuery);
  chatQueryRef.current = chatQuery;

  const searchQuery = useChatSmsInfinite(contact?.phoneNumber, sessionNumber, companyId, 20, debouncedSearchText, isSearchingMessages);

  const mutation = useChatSmsMutation();
  const resendMutation = useResendChatSmsMutation();
  const { mutate: markContactAsRead } = useMarkAsRead(sessionNumber);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const shouldScrollToBottomRef = useRef(true);
  const previousScrollHeightRef = useRef(0);
  const isLoadingOlderMessagesRef = useRef(false);

  const [highlightedMessageUid, setHighlightedMessageUid] = useState<string | null>(null);

  const [actionsPopover, setActionsPopover] = useState<{
    anchorEl: HTMLElement;
    message: SmsMessage;
    isOutgoing: boolean;
  } | null>(null);

  const [infoPopover, setInfoPopover] = useState<{
    anchorEl: HTMLElement;
    message: SmsMessage;
    isOutgoing: boolean;
  } | null>(null);

  const lastReadMessageUidRef = useRef<string | null>(null);
  const latestMessage = chatQuery.messages[chatQuery.messages.length - 1];
  const latestIncomingUid =
    latestMessage && latestMessage.from !== sessionNumber ? latestMessage.uid : null;

  useEffect(() => {
    if (!isActive || !contact?.phoneNumber || !latestIncomingUid) return;
    if (lastReadMessageUidRef.current === latestIncomingUid) return;

    lastReadMessageUidRef.current = latestIncomingUid;
    markContactAsRead(contact.phoneNumber);
  }, [isActive, latestIncomingUid, contact?.phoneNumber, markContactAsRead]);

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
    });
  };

  const normalizeText = useCallback((value: unknown) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase(), []);

  const findFocusUid = useCallback(
    (messages: SmsMessage[]) => {
      if (focusMessageUid) {
        const exactMatch = messages.find((message) => String(message.uid) === String(focusMessageUid));
        if (exactMatch) return exactMatch.uid;
      }

      const normalizedFocusText = normalizeText(focusMessageText);
      if (!normalizedFocusText) return null;

      for (let index = messages.length - 1; index >= 0; index -= 1) {
        const normalizedMessageText = normalizeText(messages[index].content?.Body);
        if (normalizedMessageText === normalizedFocusText || normalizedMessageText.includes(normalizedFocusText)) {
          return messages[index].uid;
        }
      }

      return null;
    },
    [focusMessageUid, focusMessageText, normalizeText],
  );

  const scrollToMessage = useCallback((messageUid: string) => {
    const element = messageRefs.current[messageUid];

    if (!element) return false;

    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setHighlightedMessageUid(messageUid);

    setTimeout(() => {
      setHighlightedMessageUid((current) => (current === messageUid ? null : current));
    }, 2200);

    return true;
  }, []);

  const consumeFocusMessage = useCallback(() => {
    if (!onFocusMessageConsumed) return;
    setTimeout(() => onFocusMessageConsumed(), 900);
  }, [onFocusMessageConsumed]);

  const scrollToMessageWhenRendered = useCallback(
    async (messageUid: string, attempts = MAX_SCROLL_ATTEMPTS) => {
      for (let attempt = 0; attempt < attempts; attempt += 1) {
        if (scrollToMessage(messageUid)) return true;
        await new Promise((resolve) => setTimeout(resolve, SCROLL_RETRY_DELAY_MS));
      }

      return false;
    },
    [scrollToMessage],
  );

  const focusMessageByUid = useCallback(
    async (messageUid: string) => {
      if (await scrollToMessageWhenRendered(messageUid, 2)) return true;

      let fetches = 0;

      while (fetches < MAX_FOCUS_PAGE_FETCHES && chatQueryRef.current.hasNextPage) {
        fetches += 1;

        const result = await chatQueryRef.current.fetchNextPage();
        const loadedMessages = result.data?.pages.flatMap((page) => page.data) ?? [];

        if (loadedMessages.some((item) => item.uid === messageUid)) break;
      }

      return scrollToMessageWhenRendered(messageUid);
    },
    [scrollToMessageWhenRendered],
  );

  const handleSelectSearchResult = async (message: SmsMessage) => {
    setSearchOpen(false);
    setSearchText("");
    shouldScrollToBottomRef.current = false;

    const focused = await focusMessageByUid(message.uid);

    if (!focused) {
      showSnackbar("Could not load that message in the conversation", "error");
    }
  };

  useEffect(() => {
    if (!focusMessageUid && !focusMessageText) return;
    if (chatQuery.isLoading || !chatQuery.messages.length) return;

    let cancelled = false;
    shouldScrollToBottomRef.current = false;

    const focusMessage = async () => {
      await new Promise((resolve) => setTimeout(resolve, 120));

      const visibleUid = findFocusUid(chatQueryRef.current.messages);
      if (cancelled) return;
      if (visibleUid) {
        await scrollToMessageWhenRendered(visibleUid);
        if (!cancelled) consumeFocusMessage();
        return;
      }

      let foundUid: string | null = null;
      let fetches = 0;

      while (!cancelled && !foundUid && fetches < MAX_FOCUS_PAGE_FETCHES && chatQueryRef.current.hasNextPage) {
        fetches += 1;

        const result = await chatQueryRef.current.fetchNextPage();
        const loadedMessages = result.data?.pages.flatMap((page) => page.data) ?? [];

        foundUid = findFocusUid(loadedMessages);
      }

      if (!cancelled && foundUid) {
        await scrollToMessageWhenRendered(foundUid);
      }

      if (!cancelled) consumeFocusMessage();
    };

    focusMessage();

    return () => {
      cancelled = true;
    };
  }, [
    focusMessageUid,
    focusMessageText,
    focusMessageRequestKey,
    chatQuery.isLoading,
    chatQuery.messages.length,
    findFocusUid,
    scrollToMessageWhenRendered,
    consumeFocusMessage,
  ]);

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (!chatQuery.hasNextPage) return;
    if (chatQuery.isFetchingNextPage) return;

    const TOP_THRESHOLD = 120;

    if (container.scrollTop <= TOP_THRESHOLD) {
      previousScrollHeightRef.current = container.scrollHeight;
      isLoadingOlderMessagesRef.current = true;
      shouldScrollToBottomRef.current = false;
      chatQuery.fetchNextPage();
    }
  };

  useEffect(() => {
    shouldScrollToBottomRef.current = true;
    messageRefs.current = {};
    lastReadMessageUidRef.current = null;
  }, [contact?.phoneNumber, sessionNumber]);

  useEffect(() => {
    if (isSearchingMessages) return;

    if (!chatQuery.isLoading && chatQuery.messages.length && shouldScrollToBottomRef.current) {
      scrollToBottom(false);
      shouldScrollToBottomRef.current = false;
    }
  }, [chatQuery.isLoading, chatQuery.messages.length, isSearchingMessages]);

  useLayoutEffect(() => {
    if (isSearchingMessages) return;

    const container = messagesContainerRef.current;
    if (!container) return;
    if (!isLoadingOlderMessagesRef.current) return;
    if (chatQuery.isFetchingNextPage) return;

    const previousScrollHeight = previousScrollHeightRef.current;
    const newScrollHeight = container.scrollHeight;
    const heightDifference = newScrollHeight - previousScrollHeight;

    container.scrollTop = container.scrollTop + heightDifference;

    isLoadingOlderMessagesRef.current = false;
    previousScrollHeightRef.current = 0;
  }, [chatQuery.messages.length, chatQuery.isFetchingNextPage, isSearchingMessages]);

  const handleSend = ({ to, message, files }: { to?: string; message?: string; files: File[] }) => {
    if (!message?.trim() && !files.length) return;

    mutation.mutate(
      {
        to: contact?.phoneNumber,
        from: sessionNumber,
        companyId,
        message,
        files,
      },
      {
        onSuccess: () => {
          shouldScrollToBottomRef.current = true;
          setTimeout(() => scrollToBottom(true), 50);
        },
      },
    );
  };

  const handleResend = (message: SmsMessage) => {
    if (!message.uid || resendMutation.isPending) return;

    resendMutation.mutate(message.uid, {
      onSuccess: () => {
        shouldScrollToBottomRef.current = true;
        setTimeout(() => scrollToBottom(true), 50);
      },
    });
  };

  const handleCopyMessage = async (message: SmsMessage) => {
    const body = message.content?.Body ?? "";
    if (!body.trim()) return;

    try {
      await navigator.clipboard.writeText(body);
      showSnackbar("Message copied to clipboard!", "success");
    } catch (error) {
      console.error("Failed to copy message:", error);
    } finally {
      setActionsPopover(null);
    }
  };

  const POPOVER_Z_INDEX = 2147483647;

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column" sx={{ maxHeight: { xs: "90%", sm: "100%" } }}>
      <Popover
        open={!!actionsPopover}
        anchorEl={actionsPopover?.anchorEl}
        onClose={() => setActionsPopover(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: actionsPopover?.isOutgoing ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: actionsPopover?.isOutgoing ? "right" : "left",
        }}
        disableRestoreFocus
        disablePortal={true}
        keepMounted={false}
        sx={{
          zIndex: POPOVER_Z_INDEX,
          "& .MuiPopover-paper": { zIndex: POPOVER_Z_INDEX },
        }}
        slotProps={{
          root: { sx: { zIndex: POPOVER_Z_INDEX } },
          paper: {
            elevation: 0,
            sx: {
              zIndex: POPOVER_Z_INDEX,
              borderRadius: "14px",
              mt: 0.75,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
              minWidth: 190,
              overflow: "hidden",
              "& .MuiList-root": { py: 1, px: 0.75 },
            },
          },
        }}
      >
        {actionsPopover && (
          <MenuList dense>
            {actionsPopover.message.content?.Body && (
              <MenuItem onClick={() => handleCopyMessage(actionsPopover.message)}>
                <ContentCopyIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} />
                <ListItemText primary="Copy message" />
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                setInfoPopover({
                  anchorEl: actionsPopover.anchorEl,
                  message: actionsPopover.message,
                  isOutgoing: actionsPopover.isOutgoing,
                });
                setActionsPopover(null);
              }}
            >
              <InfoOutlinedIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} />
              <ListItemText primary="Message info" />
            </MenuItem>
          </MenuList>
        )}
      </Popover>

      <Popover
        open={!!infoPopover}
        anchorEl={infoPopover?.anchorEl}
        onClose={() => setInfoPopover(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: infoPopover?.isOutgoing ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: infoPopover?.isOutgoing ? "right" : "left",
        }}
        disableRestoreFocus
        disablePortal={true}
        keepMounted={false}
        sx={{
          zIndex: POPOVER_Z_INDEX,
          "& .MuiPopover-paper": { zIndex: POPOVER_Z_INDEX },
        }}
        slotProps={{
          root: { sx: { zIndex: POPOVER_Z_INDEX } },
          paper: {
            elevation: 0,
            sx: {
              zIndex: POPOVER_Z_INDEX,
              borderRadius: "14px",
              mt: 0.75,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
              p: 1.5,
              minWidth: 180,
              overflow: "hidden",
            },
          },
        }}
      >
        {infoPopover &&
          (() => {
            const date = new Date(infoPopover.message.createdAt);
            const dayLabel = formatDayLabel(date);
            const time = date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            });

            const mediaUrls: string[] = infoPopover.message.content?.MediaUrl ?? [];

            const hasAudio = mediaUrls.some((url) => isAudioUrl(url));
            const hasVideo = mediaUrls.some((url) => isVideoUrl(url));
            const hasImage = mediaUrls.some((url) => !isAudioUrl(url) && !isVideoUrl(url));

            const rows = [
              { label: "Date", value: dayLabel },
              { label: "Time", value: time },
              { label: "From", value: infoPopover.message.from },
              ...(infoPopover.message.to ? [{ label: "To", value: infoPopover.message.to }] : []),
              ...(hasAudio ? [{ label: "Type", value: "Audio" }] : []),
              ...(hasVideo ? [{ label: "Type", value: "Video" }] : []),
              ...(hasImage ? [{ label: "Type", value: "Image" }] : []),
              {
                label: "ExternalUid",
                value: infoPopover.message.externalUid,
              },
            ];

            return (
              <Box>
                {rows.map(({ label, value }, index) => (
                  <Box key={`${label}-${index}`} display="flex" alignItems="baseline" gap={1} mt={index === 0 ? 0 : 0.75}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.65rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        minWidth: 32,
                        flexShrink: 0,
                        userSelect: "none",
                      }}
                    >
                      {label}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.primary",
                        fontSize: "0.75rem",
                        wordBreak: "break-all",
                        userSelect: "text",
                        WebkitUserSelect: "text",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          })()}
      </Popover>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1,
          py: 0.5,
          borderBottom: "1px solid",
          borderColor: theme.shell.border,
          backgroundColor: theme.shell.headerBg,
          color: theme.shell.textPrimary,
          flexShrink: 0,
          minHeight: 48,
          gap: 0.5,
        }}
      >
        {searchOpen ? (
          <>
            <IconButton
              size="small"
              onClick={() => {
                setSearchOpen(false);
                setSearchText("");
              }}
              aria-label="Close search"
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Box display="flex" alignItems="center" gap={1} flex={1} px={1.5} py={0.5} sx={{ borderRadius: "20px", bgcolor: theme.chat.inputBg }}>
              <SearchIcon
                sx={{
                  fontSize: 18,
                  color: theme.shell.textSecondary,
                  flexShrink: 0,
                }}
              />

              <input
                ref={searchInputRef}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search messages..."
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "0.875rem",
                  color: "inherit",
                  fontFamily: "inherit",
                }}
              />

              {searchText && (
                <IconButton size="small" onClick={() => setSearchText("")} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </>
        ) : (
          <>
            {!hideBack && (
              <IconButton size="small" onClick={onBack} aria-label="Back" sx={{ color: theme.shell.textSecondary }}>
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", px: 0.5 }}>
              {displayName ? (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {displayName}
                    </Typography>
                    {savedContact && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (contact) {
                            onEditContact?.({
                              ...contact,
                              contact: savedContact,
                            });
                          }
                        }}
                        sx={{
                          p: 0.25,
                          color: theme.shell.textSecondary,
                          "&:hover": {
                            color: theme.shell.accent,
                            bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: theme.shell.textSecondary,
                      lineHeight: 1.2,
                      mt: 0.25,
                    }}
                  >
                    {formatUSPhone(contact?.phoneNumber ?? "")}
                  </Typography>
                </>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatUSPhone(contact?.phoneNumber ?? "")}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      if (contact?.phoneNumber) onQuickSave?.(contact.phoneNumber, e.currentTarget);
                    }}
                    sx={{
                      p: 0.25,
                      color: theme.shell.textSecondary,
                      "&:hover": {
                        color: theme.shell.accent,
                        bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                      },
                    }}
                  >
                    <PersonAddIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              )}
            </Box>

            <IconButton
              size="small"
              onClick={() => {
                setSearchOpen(true);
                setTimeout(() => searchInputRef.current?.focus(), 50);
              }}
              aria-label="Search"
              sx={{ color: theme.shell.textSecondary }}
            >
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <IconButton
              size="small"
              aria-label="Call"
              onClick={() => makeCall(contact?.phoneNumber)}
              sx={{
                color: theme.callButton.color,
                "&:hover": { backgroundColor: theme.callButton.hoverBg },
              }}
            >
              <PhoneIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </>
        )}
      </Box>

      {isSearchingMessages ? (
        <SmsSearchResultsList
          messages={searchQuery.rawMessages}
          searchQuery={debouncedSearchText}
          sessionNumber={sessionNumber}
          isLoading={searchQuery.isLoading}
          error={searchQuery.error}
          isFetchingNextPage={searchQuery.isFetchingNextPage}
          hasNextPage={searchQuery.hasNextPage}
          onFetchNextPage={searchQuery.fetchNextPage}
          onSelectMessage={handleSelectSearchResult}
          onOpenActions={setActionsPopover}
        />
      ) : (
        <Box
          flex={1}
          p={2}
          overflow="auto"
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.chat.bg,
            background: theme.chat.pattern ? `${theme.chat.pattern}, ${theme.chat.bg}` : undefined,
            color: theme.shell.textPrimary,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: theme.chat.scrollThumb,
              borderRadius: "3px",
              "&:hover": { bgcolor: theme.shell.accent },
            },
          }}
        >
          {chatQuery.isFetchingNextPage && (
            <Box display="flex" justifyContent="center" py={1}>
              <CircularProgress size={20} />
            </Box>
          )}

          {chatQuery.isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={28} />
            </Box>
          )}

          {chatQuery.error && (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Box textAlign="center">
                <ErrorOutlineIcon sx={{ fontSize: 40, color: "error.main", mb: 1 }} />
                <Typography color="error" variant="body2">
                  Failed to load messages
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  Please try again later
                </Typography>
              </Box>
            </Box>
          )}

          {!chatQuery.isLoading && !chatQuery.error && chatQuery.messages.length === 0 && (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography sx={{ color: theme.chat.dividerText }} variant="body2">
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          )}

          {(() => {
            if (!chatQuery.messages.length) return null;

            let lastDayKey: string | null = null;

            return chatQuery.messages.map((message: SmsMessage) => {
              const isOutgoing = message.from === sessionNumber;
              const mediaUrls: string[] = message.content?.MediaUrl ?? [];
              const hasMedia = mediaUrls.length > 0;

              const date = new Date(message.createdAt);
              const msgDayKey = getDayKey(date);
              const showDivider = msgDayKey !== lastDayKey;
              lastDayKey = msgDayKey;

              const timestamp = date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              });

              const statusTooltip = isOutgoing ? getSmsStatusTooltip(message.content?.deliveryStatus, message.content?.errorMessage) : "";

              const hasFailed = isOutgoing && message.content?.deliveryStatus === "not-delivered";

              return (
                <Box
                  key={message.uid}
                  ref={(element: HTMLDivElement | null) => {
                    messageRefs.current[message.uid] = element;
                  }}
                  sx={{
                    transition: "background-color 0.25s ease",
                    borderRadius: 2,
                    bgcolor: highlightedMessageUid === message.uid ? theme.chat.highlight : "transparent",
                  }}
                >
                  {showDivider && (
                    <Box display="flex" alignItems="center" gap={1.5} my={2} sx={{ opacity: 0.6 }}>
                      <Box flex={1} height="1px" bgcolor={theme.chat.dividerLine} />

                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.chat.dividerText,
                          fontSize: "0.7rem",
                          letterSpacing: "0.04em",
                          whiteSpace: "nowrap",
                          userSelect: "none",
                        }}
                      >
                        {formatDayLabel(date)}
                      </Typography>

                      <Box flex={1} height="1px" bgcolor={theme.chat.dividerLine} />
                    </Box>
                  )}

                  <Box
                    display="flex"
                    justifyContent={isOutgoing ? "flex-end" : "flex-start"}
                    alignItems="flex-end"
                    gap={0.75}
                    mb={1}
                  >
                    {isOutgoing && (
                      <MessageDeliveryStatus
                        status={message.content?.deliveryStatus}
                        errorMessage={message.content?.errorMessage}
                        isRetrying={resendMutation.isPending && resendMutation.variables === message.uid}
                        onRetry={() => handleResend(message)}
                      />
                    )}

                    <Tooltip
                      title={statusTooltip}
                      placement={isOutgoing ? "left" : "right"}
                      enterDelay={200}
                      slotProps={{ popper: { sx: { zIndex: "2147483647 !important" } } }}
                    >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        pr: isOutgoing ? 4.25 : 2.5,
                        pl: isOutgoing ? 2.5 : 4.25,
                        borderRadius: isOutgoing ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
                        maxWidth: "60%",
                        bgcolor: isOutgoing ? theme.bubbleOut.bg : theme.bubbleIn.bg,
                        color: isOutgoing ? theme.bubbleOut.color : theme.bubbleIn.color,
                        border: hasFailed ? "1px dashed" : "1px solid",
                        borderColor: hasFailed ? "error.main" : isOutgoing ? theme.bubbleOut.border : theme.bubbleIn.border,
                        opacity: hasFailed ? 0.55 : 1,
                        boxShadow: "0px 2px 4px rgba(0,0,0,0.02)",
                        wordBreak: "break-word",
                        position: "relative",
                        cursor: "default",
                        userSelect: "text",
                        WebkitUserSelect: "text",
                        WebkitTouchCallout: "default",
                        "&:hover .message-actions": {
                          opacity: 1,
                          pointerEvents: "auto",
                        },
                      }}
                    >
                      <IconButton
                        className="message-actions"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setActionsPopover({
                            anchorEl: event.currentTarget,
                            message,
                            isOutgoing,
                          });
                        }}
                        sx={{
                          position: "absolute",
                          top: 5,
                          right: isOutgoing ? 5 : "auto",
                          left: isOutgoing ? "auto" : 5,
                          width: 24,
                          height: 24,
                          opacity: { xs: 1, sm: 0 },
                          pointerEvents: { xs: "auto", sm: "none" },
                          bgcolor: theme.shell.bg,
                          color: theme.shell.textSecondary,
                          boxShadow: "0 1px 4px rgba(0,0,0,0.14)",
                          zIndex: 2,
                          transition: "opacity 0.15s ease",
                          "&:hover": {
                            bgcolor: theme.shell.bg,
                            color: theme.shell.textPrimary,
                          },
                        }}
                      >
                        <MoreHorizIcon sx={{ fontSize: 16 }} />
                      </IconButton>

                      {message.content?.Body && (
                        <Typography
                          variant="body2"
                          mb={hasMedia ? 1 : 0}
                          sx={{
                            userSelect: "text",
                            WebkitUserSelect: "text",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {message.content.Body}
                        </Typography>
                      )}

                      {mediaUrls.map((url, index) => {
                        const audio = isAudioUrl(url);
                        const video = !audio && isVideoUrl(url);

                        return audio ? (
                          <AudioMessageBubble key={`${url}-${index}`} src={url} isOutgoing={isOutgoing} />
                        ) : video ? (
                          <Box
                            key={`${url}-${index}`}
                            component="video"
                            src={url}
                            controls
                            preload="metadata"
                            sx={{
                              display: "block",
                              maxWidth: "100%",
                              borderRadius: 1.5,
                              mt: 0.5,
                              outline: "none",
                            }}
                          />
                        ) : (
                          <Box
                            key={`${url}-${index}`}
                            component="img"
                            src={url}
                            alt="media"
                            sx={{
                              display: "block",
                              maxWidth: "100%",
                              borderRadius: 1.5,
                              mt: 0.5,
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={() => window.open(url, "_blank")}
                          />
                        );
                      })}

                      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5} mt={0.5}>
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.7,
                            userSelect: "none",
                          }}
                        >
                          {timestamp}
                        </Typography>
                      </Box>
                    </Box>
                    </Tooltip>
                  </Box>
                </Box>
              );
            });
          })()}

          <div ref={messagesEndRef} />
        </Box>
      )}

      {!isSearchingMessages && (
        <MessageInput
          to={contact?.phoneNumber}
          isSending={mutation.isPending}
          onSend={handleSend}
          draftKey={buildChatDraftKey({
            companyId,
            scope: "sms",
            id: String(contact?.phoneNumber ?? "").replace(/\D/g, "") || null,
          })}
        />
      )}
    </Box>
  );
}
